import { FetchError, ofetch } from 'ofetch';

import { createSingleton } from '@deepidoo/deesplay/utils';

import { useApiError, useToast } from '../../composables';
import { router } from '../../plugins/router';
import { useOrganizationStore } from '../../stores';
import { toastMessages } from '../../utils';
import { useSession } from './useSession';

enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE'
};

export interface HttpQueryParam {
    key: string;
    value?: string[] | number[] | boolean[] | string | number | boolean;
};

export const useFetchClient = createSingleton((): {
    publicClient: ReturnType<typeof ofetch.create>;
    constructUrl: (url: string, params?: HttpQueryParam[]) => string;
    postWithFile: <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    putWithFile: <T>(url: string, file?: File, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    get: <T>(url: string, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    post: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    put: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    patch: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    remove: (url: string, params?: HttpQueryParam[], options?: RequestInit) => Promise<void>;
} => {
    const { checkSession, clearSession, isAuthenticated, isRefreshable, refreshSession } = useSession();
    const { handleApiError } = useApiError();
    const { showToast } = useToast();

    const redirectToLogin = (): void => {
        clearSession();
        router.value?.push({
            name: 'login',
            query: {
                redirect: router.value.currentRoute.fullPath
            }
        });
    };

    const isUnauthorized = (error: unknown): boolean => {
        return error instanceof FetchError && error.status === 401;
    };

    const publicClient = ofetch.create({
        onRequest: ({ options, request }) => {
            if (request.toString().startsWith(import.meta.env.VITE_API_BASE_URL)) {
                options.headers.set('X-Client-Id', import.meta.env.VITE_API_CLIENT_ID);
            }
        }
    });

    const fetchClient = ofetch.create({
        onRequest: ({ options, request }) => {
            if (
                request.toString().startsWith(import.meta.env.VITE_API_BASE_URL) ||
                request.toString().startsWith(import.meta.env.VITE_CONVERTER_URL)
            ) {
                const { currentOrganization } = useOrganizationStore();
                const { accessToken, isAuthenticated } = useSession();

                if (currentOrganization && !options.headers.has('X-Organization-Id')) {
                    options.headers.set('X-Organization-Id', currentOrganization.id.toString());
                }
                if (isAuthenticated.value) {
                    options.headers.set('Authorization', `Bearer ${accessToken.value}`);
                }
            }
        },
        onResponseError: ({ response }): void => {
            if (response.status !== 401) {
                handleApiError(response.status);
            }
        }
    });

    const fetchWithAuthRetry = async <T>(request: () => Promise<T>): Promise<T> => {
        try {
            return await request();
        }
        catch (error) {
            if (!isUnauthorized(error)) {
                throw error;
            }
            if (!await refreshSession()) {
                redirectToLogin();
                throw new Error('Not authenticated');
            }
            try {
                return await request();
            }
            catch (retryError) {
                if (isUnauthorized(retryError)) {
                    handleApiError(401);
                }
                throw retryError;
            }
        }
    };

    const timedRequest = async <T>(
        method: HttpMethod,
        url: string,
        body?: any,
        params?: HttpQueryParam[],
        options?: RequestInit
    ): Promise<T> => {
        if (!router.value) {
            throw new Error('Router has not been initialized');
        }
        checkSession();
        if (!isRefreshable.value) {
            redirectToLogin();
            throw new Error('Not authenticated');
        }
        if (!isAuthenticated.value) {
            if (!await refreshSession()) {
                redirectToLogin();
                throw new Error('Not authenticated');
            }
        }

        const slowNetwork = setTimeout(() => {
            showToast(toastMessages.warning.networkSlow);
        }, 30000);

        try {
            return await fetchWithAuthRetry(() => fetchClient(constructUrl(url, params), {
                method,
                body: body ? JSON.stringify(body) : undefined,
                ...options
            }));
        }
        finally {
            clearTimeout(slowNetwork);
        }
    };

    const constructUrl = (url: string, params?: HttpQueryParam[]): string => {
        let fullUrl = url;
        if (params && params.length > 0) {
            const queryString = params
                .filter(param => {
                    if (param.value === undefined) {
                        return false;
                    }
                    if (typeof param.value === 'string' && param.value.trim() === '') {
                        return false;
                    }
                    if (Array.isArray(param.value) && param.value.length === 0) {
                        return false;
                    }
                    return true;
                })
                .map(param => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value as string | number | boolean)}`)
                .join('&');
            fullUrl += `?${queryString}`;
        }
        return fullUrl;
    };

    const postWithFile = <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        const formData = new FormData();
        if (file) {
            formData.append('file', file);
        }
        if (body) {
            formData.append('body', JSON.stringify(body));
        }

        return fetchWithAuthRetry(() => fetchClient(constructUrl(url, params), {
            method: HttpMethod.POST,
            body: formData,
            ...options
        }));
    };

    const putWithFile = <T>(url: string, file?: File, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return fetchWithAuthRetry(() => fetchClient(constructUrl(url, params), {
            method: HttpMethod.PUT,
            body: file,
            ...options
        }));
    };

    const get = <T>(url: string, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.GET, url, undefined, params, options);
    };

    const post = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.POST, url, body, params, options);
    };

    const put = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.PUT, url, body, params, options);
    };

    const patch = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.PATCH, url, body, params, options);
    };

    const remove = (url: string, params?: HttpQueryParam[], options?: RequestInit): Promise<void> => {
        return timedRequest(HttpMethod.DELETE, url, undefined, params, options);
    };

    return {
        publicClient,
        constructUrl,
        postWithFile,
        putWithFile,
        get,
        post,
        put,
        patch,
        remove
    };
});
